import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { SpareParts } from '@ecs-features/result/spare-parts-result/spare-parts.interface';
import { isStringArrayWithEntries } from '@ecs-helpers';
import {
    isFreeTextDataResult,
    isFreeTextResult,
    isSparePartsDataResult,
    isSparePartsResult,
    isTypeDesignationDataResult,
    isTypeDesignationResult,
    needToCheckForOrderNumberItems,
} from '@ecs-services/search/search.helper';
import {
    FreeTextDataResult,
    FreeTextSearchResult,
    SearchResultType,
    SparePartsDataResult,
    TypeDesignationDataResult,
    TypeDesignationSearchResult,
} from '@ecs-services/search/search.interface';
import { PlantsWithSpareParts, SparePartService } from '@ecs-services/spare-part/spare-part.service';
import { searchTermChanged } from '@ecs-stores/store.helper';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { iif, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, takeUntil, withLatestFrom } from 'rxjs/operators';
import { OrderNumberService } from '../../../../services/order-number/order-number.service';
import { SearchStoreService } from '../../../../services/search/search-store.service';
import { SearchEffectsService } from '../../search-effects.service';
import * as SearchActions from '../../search.actions';
import { DataStage } from '../../search.reducer';
import * as SparePartActions from './spare-part.actions';

@Injectable()
export class SparePartEffects {
    public getSpareParts$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SearchActions.receiveSearchResult),
            map(({ payload: { searchResult } }) => searchResult),
            filter(isSparePartsResult),
            map(({ spareParts }) => spareParts),
            filter((spareParts): spareParts is string[] => isStringArrayWithEntries(spareParts)),
            switchMap((spareParts) =>
                this.sparePartService.getSpareParts(spareParts).pipe(
                    takeUntil(searchTermChanged(this.searchStore)),
                    catchError(() => of(null))
                )
            ),
            map((sparePartList) =>
                sparePartList
                    ? SearchActions.receiveSearchResult({
                          payload: {
                              searchResult: {
                                  type: SearchResultType.SPARE_PARTS_DATA,
                                  spareParts: sparePartList.spareParts,
                                  errors: sparePartList.errors ?? [],
                              },
                              searchError: null,
                          },
                          userRequested: true,
                      })
                    : this.getNoResultsError([])
            )
        )
    );

    public getOrderNumber$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SearchActions.receiveSearchResult),
            map(({ payload: { searchResult, searchError } }) => ({ searchResult, searchError })),
            mergeMap(({ searchResult, searchError }) =>
                iif(
                    () => searchResult !== undefined && searchResult !== null,
                    of(searchResult).pipe(
                        filter(isSparePartsResult),
                        map(({ spareParts }) => spareParts),
                        filter(
                            (spareParts): spareParts is string[] =>
                                isStringArrayWithEntries(spareParts) && spareParts.length === 1
                        ),
                        map((spareParts) => ({ searchResult, searchTerm: spareParts[0] }))
                    ),
                    of(searchError).pipe(
                        filter(needToCheckForOrderNumberItems),
                        withLatestFrom(this.searchStore.getSearchTerm()),
                        map(([_, searchTerms]) => searchTerms),
                        filter(
                            (searchTerms) => isStringArrayWithEntries(searchTerms) && searchTerms.length === 1
                        ),
                        map((searchTerms) => ({ searchResult, searchTerm: searchTerms[0] }))
                    )
                )
            ),
            switchMap(({ searchTerm, searchResult }) =>
                this.orderNumberService.getOrder(searchTerm).pipe(
                    takeUntil(searchTermChanged(this.searchStore)),
                    catchError(() => of(null)),
                    map((orderNumberSearchResult) => ({ orderNumberSearchResult, searchResult }))
                )
            ),
            map(({ orderNumberSearchResult, searchResult }) => {
                if (orderNumberSearchResult !== null) {
                    this.router.navigate([{ outlets: { result: ['spare-parts'] } }], {
                        queryParamsHandling: 'preserve',
                        skipLocationChange: true,
                    });

                    return SearchActions.receiveOrderNumberSearchResult({
                        payload: {
                            orderNumberSearchResult,
                        },
                    });
                } else {
                    if (searchResult === null) {
                        this.router.navigate([{ outlets: { result: null } }], {
                            queryParamsHandling: 'preserve',
                            skipLocationChange: true,
                        });
                    }

                    return this.searchEffectsService.updateOrderNumberItemChecked(true);
                }
            })
        )
    );

    public getSparePartsFreeText$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SearchActions.receiveSearchResult),
            map(({ payload: { searchResult } }) => searchResult),
            filter(isFreeTextResult),
            filter(
                (searchResult): searchResult is FreeTextSearchResult =>
                    Array.isArray(searchResult.spareParts) && searchResult.spareParts.length > 0
            ),
            switchMap((searchResult) =>
                this.sparePartService.getSpareParts(searchResult.spareParts).pipe(
                    takeUntil(searchTermChanged(this.searchStore)),
                    catchError(() => of(null)),
                    map((spareParts) =>
                        spareParts
                            ? SearchActions.receiveSearchResult({
                                  payload: {
                                      searchResult: {
                                          type: SearchResultType.FREE_TEXT_DATA,
                                          ...spareParts,
                                      },
                                      searchError: null,
                                  },
                                  userRequested: true,
                              })
                            : this.getNoResultsError([])
                    )
                )
            )
        )
    );

    public getSparePartsTypeDesignation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SearchActions.receiveSearchResult),
            map(({ payload: { searchResult } }) => searchResult),
            filter(isTypeDesignationResult),
            filter((searchResult): searchResult is TypeDesignationSearchResult & { spareParts: string[] } =>
                isStringArrayWithEntries(searchResult.spareParts)
            ),
            switchMap((searchResult) =>
                this.sparePartService.getSpareParts(searchResult.spareParts).pipe(
                    takeUntil(searchTermChanged(this.searchStore)),
                    catchError(() => of(null)),
                    map((spareParts) =>
                        spareParts
                            ? SearchActions.receiveSearchResult({
                                  payload: {
                                      searchResult: {
                                          /**
                                           * Treat `TypeDesignationSearchResult` without `documents`-Property like `FreeTextSearchResult`.
                                           */
                                          type: searchResult.sparePartLists?.length
                                              ? SearchResultType.TYPE_DESIGNATION_DATA
                                              : SearchResultType.FREE_TEXT_DATA,
                                          ...spareParts,
                                          sparePartLists: searchResult.sparePartLists,
                                      },
                                      searchError: null,
                                  },
                                  userRequested: true,
                              })
                            : this.getNoResultsError([])
                    )
                )
            )
        )
    );

    /**
     * `FREE_TEXT` and `SPARE_PARTS` share the same steps:
     * 1. Search for ID (List of ids)
     * 2. Get data (List of spare parts with details)
     * 3. Get availability (the following effect)
     */
    public requestAvailabilitySpareParts$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SearchActions.receiveSearchResult),
            map(({ payload: { searchResult } }) => searchResult),
            filter(
                (
                    searchResult
                ): searchResult is SparePartsDataResult | FreeTextDataResult | TypeDesignationDataResult =>
                    isSparePartsDataResult(searchResult) ||
                    isFreeTextDataResult(searchResult) ||
                    isTypeDesignationDataResult(searchResult)
            ),
            map(({ spareParts }) => spareParts),
            filter((spareParts): spareParts is SpareParts => !!spareParts),
            map((spareParts) =>
                spareParts.filter(
                    (sparePart) => sparePart.availabilityCode === '00' || sparePart.availabilityCode === '04'
                )
            ),
            switchMap((spareParts) => {
                const plantsWithSparePartsHelper = spareParts.reduce(
                    (plantsWithSpareParts: PlantsWithSpareParts, sparePart) => {
                        const existing = plantsWithSpareParts.find(
                            (plantWithSparePart) => plantWithSparePart.plantId === sparePart.plantId
                        );
                        if (existing) {
                            existing.spareParts = [
                                ...existing.spareParts,
                                { id: sparePart.id, availabilityCode: sparePart.availabilityCode },
                            ];
                        } else {
                            plantsWithSpareParts = [
                                ...plantsWithSpareParts,
                                {
                                    plantId: sparePart.plantId,
                                    spareParts: [
                                        { id: sparePart.id, availabilityCode: sparePart.availabilityCode },
                                    ],
                                },
                            ];
                        }

                        return plantsWithSpareParts;
                    },
                    []
                );

                return this.sparePartService
                    .getSparePartsAvailability(plantsWithSparePartsHelper)
                    .pipe(takeUntil(searchTermChanged(this.searchStore)));
            }),
            map((sparePartsWithAvailability) =>
                SparePartActions.receiveSparePartsAvailability({
                    payload: {
                        sparePartsWithAvailability,
                    },
                })
            )
        )
    );

    public updateDataStage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SearchActions.receiveSearchResult),
            map((action) => {
                switch (action.payload.searchResult?.type) {
                    case SearchResultType.SPARE_PARTS:
                    case SearchResultType.FREE_TEXT:
                        return SearchActions.updateStage({ dataStage: DataStage.INTERMEDIATE });
                    case SearchResultType.SPARE_PARTS_DATA:
                    case SearchResultType.FREE_TEXT_DATA:
                        return SearchActions.updateStage({ dataStage: DataStage.FINISHED });
                    default:
                        return SearchActions.noOpAction();
                }
            })
        )
    );

    public constructor(
        private actions$: Actions,
        private sparePartService: SparePartService,
        private searchEffectsService: SearchEffectsService,
        private orderNumberService: OrderNumberService,
        private searchStore: SearchStoreService,
        private router: Router
    ) {}

    private getNoResultsError(searchTerm: string[], needToCheckForOrderItems = false) {
        return this.searchEffectsService.getNoResultsError(searchTerm, needToCheckForOrderItems);
    }
}
